"use client";

import { Wallet, ShoppingCart, CreditCard, Calendar, Tag, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface DashboardCardsProps {
  totalOrders: number;
  totalTransactions: number;
  walletBalance: number;
  ticketCount: number;
}

const DashboardCards = ({ totalOrders, totalTransactions, walletBalance, ticketCount }: DashboardCardsProps) => {
    const stats = [
        {
            title: "تعداد کل خریدها",
            value: `${totalOrders}+`,
            change: "+۱۲٪", // This could be calculated based on previous month's data
            trend: "up",
            icon: <ShoppingCart className="w-6 h-6 text-primary" />,
            color: "from-purple-500/20 to-blue-500/20",
            borderColor: "border-purple-500/30"
        },
        {
            title: "مجموع تراکنش‌ها",
            value: `${totalTransactions.toLocaleString()} تومان`,
            change: "+۸٪", // This could be calculated based on previous month's data
            trend: "up",
            icon: <CreditCard className="w-6 h-6 text-primary" />,
            color: "from-green-500/20 to-teal-500/20",
            borderColor: "border-green-500/30"
        },
        {
            title: "موجودی کیف پول",
            value: `${walletBalance.toLocaleString()} تومان`,
            change: "-۳٪", // This could be calculated based on previous month's data
            trend: "down",
            icon: <Wallet className="w-6 h-6 text-primary" />,
            color: "from-amber-500/20 to-orange-500/20",
            borderColor: "border-amber-500/30"
        },
        {
            title: "تیکت ها",
            value: ticketCount.toString(),
            change: "0٪", // This could be calculated based on previous month's data
            trend: "neutral",
            icon: <Tag className="w-6 h-6 text-primary" />,
            color: "from-pink-500/20 to-rose-500/20",
            borderColor: "border-pink-500/30"
        },
    ];

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
                <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    className={cn(
                        "relative flex cursor-default flex-col p-6 rounded-2xl overflow-hidden",
                        "bg-gradient-to-br from-background/95 to-background/85 backdrop-blur-xl",
                        "border border-white/10 shadow-lg",
                        "transition-all duration-300"
                    )}
                >
                    {/* Glowing background effect */}
                    <div className="absolute inset-0 overflow-hidden">
                        <div className={cn(
                            "absolute -top-10 -left-10 w-40 h-40 rounded-full blur-2xl opacity-50",
                            "bg-gradient-to-br", stat.color
                        )} />
                        <div className={cn(
                            "absolute -bottom-10 -right-10 w-40 h-40 rounded-full blur-2xl opacity-50",
                            "bg-gradient-to-br", stat.color
                        )} />
                    </div>
                    
                    {/* Top section with icon and title */}
                    <div className="flex items-center justify-between mb-4 relative z-10">
                        <div className="bg-primary/10 p-2 rounded-lg border border-primary/20 backdrop-blur-sm">
                            {stat.icon}
                        </div>
                        <div className={cn(
                            "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                            stat.trend === "up" ? "bg-green-500/10 text-green-400 border border-green-500/20" :
                            stat.trend === "down" ? "bg-red-500/10 text-red-400 border border-red-500/20" :
                            "bg-gray-500/10 text-gray-400 border border-gray-500/20"
                        )}>
                            {stat.trend === "up" && <ArrowUpRight className="w-3 h-3" />}
                            {stat.trend === "down" && <ArrowDownRight className="w-3 h-3" />}
                            {stat.change}
                        </div>
                    </div>
                    
                    {/* Value */}
                    <div className="relative z-10">
                        <h3 className="text-2xl font-bold text-white mb-1">{stat.value}</h3>
                        <p className="text-sm text-gray-400">{stat.title}</p>
                    </div>
                    
                    {/* Animated border effect */}
                    <div className={cn(
                        "absolute inset-0 rounded-2xl border-2 opacity-0 transition-opacity duration-300",
                        stat.borderColor
                    )} />
                    
                    {/* Hover effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </motion.div>
            ))}
        </div>
    );
};

export default DashboardCards;
